import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { ChatSidebar } from './ChatSidebar';
import { useDsTranslate } from '../context/DsProvider';
import type { DocumentReview } from '../types/review';
import styles from './ChatWidget.module.scss';

interface ChatWidgetProps {
  targetType: string;
  targetId: string;
  reviews: DocumentReview[];
  /**
   * - `author`: workpay author, session-authed
   * - `reviewer`: external reviewer, token-authed
   * - `reviewer-internal`: session-authed INTERNAL reviewer — requires `reviewId`
   */
  mode: 'author' | 'reviewer' | 'reviewer-internal';
  /** Required for `mode='reviewer'`. */
  token?: string;
  /** Required for `mode='reviewer-internal'`. */
  reviewId?: string;
  /**
   * Callback fired after a successful note send. When provided, the widget's
   * built-in cache-invalidation is skipped and the consumer owns refresh.
   * Use this whenever your consumer query keys diverge from the built-in
   * `['ds-reviews', ...]` / `['ds-review-*', token]` structure.
   */
  onNoteSent?: () => void;
  defaultExpanded?: boolean;
  positioning?: 'fixed' | 'absolute';
}

function getLastSeenKey(targetType: string, targetId: string) {
  return `chat_last_seen_${targetType}_${targetId}`;
}

export function ChatWidget({
  targetType, targetId, reviews, mode, token, reviewId, onNoteSent,
  defaultExpanded = false, positioning = 'fixed',
}: ChatWidgetProps) {
  const t = useDsTranslate();
  const [expanded, setExpanded] = useState(defaultExpanded);

  const activityCount = useMemo(
    () => reviews.reduce((sum, r) => sum + (r.activities?.length ?? 0), 0),
    [reviews],
  );

  const [lastSeenCount, setLastSeenCount] = useState(() => {
    try {
      const stored = localStorage.getItem(getLastSeenKey(targetType, targetId));
      return stored ? parseInt(stored, 10) : 0;
    } catch {
      return 0;
    }
  });

  const unreadCount = Math.max(0, activityCount - lastSeenCount);
  const hasUnread = unreadCount > 0 && !expanded;

  const handleExpand = useCallback(() => {
    setExpanded(true);
    setLastSeenCount(activityCount);
    localStorage.setItem(getLastSeenKey(targetType, targetId), String(activityCount));
  }, [activityCount, targetType, targetId]);

  useEffect(() => {
    if (!expanded) return;
    setLastSeenCount(activityCount);
    localStorage.setItem(getLastSeenKey(targetType, targetId), String(activityCount));
  }, [expanded, activityCount, targetType, targetId]);

  useEffect(() => {
    if (!expanded) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setExpanded(false); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [expanded]);

  // Draggable pill position
  const [pillPos, setPillPos] = useState<{ x: number; y: number } | null>(null);
  const dragRef = useRef<{
    offsetX: number; offsetY: number;
    parentLeft: number; parentTop: number; parentWidth: number; parentHeight: number;
    initX: number; initY: number; startX: number; startY: number; dragged: boolean;
  } | null>(null);
  const suppressClickRef = useRef(false);
  const pillRef = useRef<HTMLButtonElement>(null);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    const el = pillRef.current;
    if (!el) return;
    el.setPointerCapture(e.pointerId);
    const rect = el.getBoundingClientRect();
    const parent = el.offsetParent as HTMLElement | null;
    const parentRect = parent
      ? parent.getBoundingClientRect()
      : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
    dragRef.current = {
      offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top,
      parentLeft: parentRect.left, parentTop: parentRect.top,
      parentWidth: parentRect.width, parentHeight: parentRect.height,
      initX: rect.left - parentRect.left, initY: rect.top - parentRect.top,
      startX: e.clientX, startY: e.clientY, dragged: false,
    };
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    if (!d.dragged && Math.abs(e.clientX - d.startX) < 4 && Math.abs(e.clientY - d.startY) < 4) return;
    if (!d.dragged) { d.dragged = true; setPillPos({ x: d.initX, y: d.initY }); return; }
    const el = pillRef.current;
    if (!el) return;
    const w = el.offsetWidth; const h = el.offsetHeight;
    const x = e.clientX - d.offsetX - d.parentLeft;
    const y = e.clientY - d.offsetY - d.parentTop;
    setPillPos({ x: Math.max(0, Math.min(d.parentWidth - w, x)), y: Math.max(0, Math.min(d.parentHeight - h, y)) });
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    pillRef.current?.releasePointerCapture(e.pointerId);
    if (d.dragged) suppressClickRef.current = true;
    dragRef.current = null;
  }, []);

  const handlePillClick = useCallback(() => {
    if (suppressClickRef.current) { suppressClickRef.current = false; return; }
    handleExpand();
  }, [handleExpand]);

  const pillStyle = pillPos
    ? { left: pillPos.x, top: pillPos.y, right: 'auto' as const, bottom: 'auto' as const }
    : undefined;

  const panelStyle = pillPos
    ? (() => {
        const panelW = 360; const panelH = 480;
        const el = pillRef.current;
        const parent = el?.offsetParent as HTMLElement | null;
        const boundsW = parent ? parent.clientWidth : window.innerWidth;
        const boundsH = parent ? parent.clientHeight : window.innerHeight;
        let left = pillPos.x;
        let top = pillPos.y - panelH - 8;
        if (top < 0) top = pillPos.y + (el?.offsetHeight ?? 40) + 8;
        if (left + panelW > boundsW) left = boundsW - panelW - 8;
        if (left < 8) left = 8;
        if (top + panelH > boundsH) top = boundsH - panelH - 8;
        if (top < 8) top = 8;
        return { left, top, right: 'auto' as const, bottom: 'auto' as const };
      })()
    : undefined;

  return (
    <>
      {!expanded && (
        <button
          ref={pillRef}
          className={`${styles.pill} ${positioning === 'absolute' ? styles.pillAbsolute : ''} ${hasUnread ? styles.pillUnread : ''}`}
          onClick={handlePillClick}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          style={pillStyle}
          type="button"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 10c0 .367-.13.68-.39.94-.26.26-.573.393-.943.393H4.667L2 14V3.333c0-.366.13-.68.39-.94.26-.26.574-.393.944-.393h9.333c.37 0 .683.13.943.39.26.26.39.574.39.943V10z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className={styles.pillLabel}>{t('K_DS_CHAT', 'Chat')}</span>
          {activityCount > 0 && (
            <span className={`${styles.pillCount} ${hasUnread ? styles.pillCountUnread : ''}`}>
              {hasUnread ? unreadCount : activityCount}
            </span>
          )}
        </button>
      )}

      {expanded && (
        <div className={`${styles.panel} ${positioning === 'absolute' ? styles.panelAbsolute : ''}`} style={panelStyle}>
          <div className={styles.panelHeader}>
            <span className={styles.panelTitle}>{t('K_DS_CONVERSATION', 'Conversation')}</span>
            <button className={styles.minimizeBtn} onClick={() => setExpanded(false)} type="button" aria-label={t('K_DS_MINIMIZE_CHAT', 'Minimize chat')}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 7h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
            </button>
          </div>
          <div className={styles.panelBody}>
            <ChatSidebar
              targetType={targetType}
              targetId={targetId}
              reviews={reviews}
              mode={mode}
              token={token}
              reviewId={reviewId}
              onNoteSent={onNoteSent}
              hideHeader
            />
          </div>
        </div>
      )}
    </>
  );
}

